import { baseApiService } from '@qapline/api';
import i18n from '@qapline/translations/i18next';
import { config } from 'config';
import { DispatcherSearchResponse } from 'modules/dispatcherSearch/types';
import { SearchCarrierRequest } from 'modules/form';
import { QueryParams } from 'types/query-params';

import {
    CarrierNewSchema,
    carrierNewSchema,
    type CarrierPageSchema,
    carrierPageSchema,
    CarrierSchema,
    GetCarrierListSchema,
    PostOnboardingEmailSchema,
    VehicleFeaturesList,
    vehicleFeaturesList,
} from '../types';
import { TransformedCarrierData } from '../utils/FormattingCarrierData/types';

export const carrierApi = baseApiService.injectEndpoints({
    endpoints: (builder) => ({
        getCarriers: builder.query<CarrierPageSchema, QueryParams<CarrierSchema>>({
            providesTags: ['Carriers'],
            query: (params) => ({
                url: config.api.endpoints.carrier.list,
                params,
            }),
            transformResponse: (res: CarrierPageSchema) => {
                const validatedSchema = carrierPageSchema(i18n.t).safeParse(res);
                if (!validatedSchema.success) {
                    console.warn('Validation failed in getCarrier');
                }
                return res;
            },
        }),
        getOneCarrier: builder.query<CarrierNewSchema, { id: string }>({
            providesTags: (_result, _error, arg) => [{ type: 'CarrierItem', id: arg.id }],
            query: ({ id }) => ({
                url: `${config.api.endpoints.carrier.list}/${id}`,
            }),
            transformResponse: (res: CarrierNewSchema) => {
                // used for back compatibility; almost all carriers are incorrect
                const validatedSchema = carrierNewSchema(i18n.t).safeParse(res);
                if (!validatedSchema.success) {
                    console.warn('Validation failed in getOneCarrier');
                }
                return res;
            },
        }),
        postNewCarrier: builder.mutation<CarrierNewSchema, { data: TransformedCarrierData }>({
            query: ({ data }) => ({
                url: config.api.endpoints.carrier.create,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Carriers']),
        }),
        putUpdatedCarrier: builder.mutation<CarrierNewSchema, { data: TransformedCarrierData; id: number }>({
            query: ({ data, id }) => ({
                url: `${config.api.endpoints.carrier}/${id}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (_, error, arg) => (error ? [] : ['Carriers', { type: 'CarrierItem', id: arg.id }]),
        }),
        deleteUpdatedCarrier: builder.mutation<CarrierNewSchema, { id: number }>({
            query: ({ id }) => ({
                url: `${config.api.endpoints.carrier}/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Carriers']),
        }),
        getCarrierList: builder.query<GetCarrierListSchema, void>({
            providesTags: ['Carriers'],
            query: () => ({
                url: config.api.endpoints.carrier.getList,
                method: 'GET',
            }),
        }),
        postOnboardingEmail: builder.mutation<{ [email: string]: boolean }, PostOnboardingEmailSchema>({
            query: (body) => ({
                url: config.api.endpoints.onboardingEmail,
                method: 'POST',
                body,
            }),
            invalidatesTags: (_, error, arg) =>
                error ? [] : ['Carriers', { type: 'CarrierItem', id: arg.carrier_id }],
        }),
        getVehicleFeatures: builder.query<VehicleFeaturesList, null>({
            query: () => ({
                url: `${config.api.endpoints.vehicleType}`,
            }),
            transformResponse: (res: any[]) => {
                // Transform from API format (vehicleTypeVehicleFeatures with nested vehicleFeature)
                // to frontend format (vehicleFeatures with flat feature + vehicleFeature_id)
                const transformed = res.map((vehicleType) => ({
                    vehicleType_id: vehicleType.vehicleType_id,
                    type: vehicleType.name,
                    vehicleFeatures: (vehicleType.vehicleTypeVehicleFeatures || []).map(
                        (vtf: { vehicleFeature_id: number; vehicleFeature?: { name: string } }) => ({
                            vehicleFeature_id: vtf.vehicleFeature_id,
                            feature: vtf.vehicleFeature?.name ?? '',
                        }),
                    ),
                }));
                const validatedSchema = vehicleFeaturesList().parse(transformed);
                return validatedSchema;
            },
        }),
        searchCarriers: builder.query<
            DispatcherSearchResponse,
            { body: SearchCarrierRequest; aborter?: () => AbortSignal }
        >({
            providesTags: ['Carriers'],
            query: ({ body, aborter }: { body: SearchCarrierRequest; aborter?: () => AbortSignal }) => {
                const signal = aborter?.();

                return {
                    url: config.api.endpoints.dispatcherSearch,
                    method: 'POST',
                    body,
                    signal,
                };
            },
        }),
    }),
});

export const {
    useGetCarriersQuery,
    useLazyGetCarriersQuery,
    useGetOneCarrierQuery,
    useLazyGetOneCarrierQuery,
    usePostNewCarrierMutation,
    usePutUpdatedCarrierMutation,
    useDeleteUpdatedCarrierMutation,
    useGetCarrierListQuery,
    usePostOnboardingEmailMutation,
    useGetVehicleFeaturesQuery,
    useLazySearchCarriersQuery,
} = carrierApi;
